import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Heart,
  AlertCircle,
  Lightbulb,
  Phone,
  ArrowRight,
  CheckCircle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { analyzeSymptoms, getSeekHelpGuidance, generateSummary } from '@/utils/symptomAnalyzer';

/**
 * Non-Diagnostic AI Symptom Checker
 * Translates symptoms into lifestyle-related insights
 * IMPORTANT: No medical diagnosis, supportive tone only
 */

const SymptomChecker = () => {
  const navigate = useNavigate();
  
  // Symptom selection
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  
  // Lifestyle context
  const [lifestyleContext, setLifestyleContext] = useState({
    slept_well: true,
    drank_water: true,
    moved_body: true,
    ate_well: true,
    stress_level: 'low'
  });

  // Results
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const availableSymptoms = [
    { id: 'fatigue', label: 'Fatigue or tiredness', icon: '😴' },
    { id: 'headache', label: 'Headache', icon: '🤕' },
    { id: 'dizziness', label: 'Dizziness', icon: '💫' },
    { id: 'stomach_upset', label: 'Stomach upset', icon: '🤢' },
    { id: 'difficulty_sleeping', label: 'Difficulty sleeping', icon: '🌙' },
    { id: 'anxiety', label: 'Feeling anxious', icon: '😰' },
    { id: 'brain_fog', label: 'Brain fog or difficulty focusing', icon: '🧠' },
    { id: 'muscle_aches', label: 'Muscle aches or stiffness', icon: '💪' }
  ];

  const handleSymptomToggle = (symptomId) => {
    setSelectedSymptoms(prev =>
      prev.includes(symptomId)
        ? prev.filter(s => s !== symptomId)
        : [...prev, symptomId]
    );
  };

  const handleAnalyze = () => {
    if (selectedSymptoms.length === 0) {
      toast.error('Please select at least one symptom');
      return;
    }

    // Analyze symptoms
    const analysis = analyzeSymptoms(selectedSymptoms, lifestyleContext);
    const seekHelp = getSeekHelpGuidance(analysis.severity, analysis.hasRedFlag);
    const summary = generateSummary(analysis.insights);

    setResults({
      summary,
      insights: analysis.insights,
      actions: analysis.actions,
      seekHelp
    });
    setShowResults(true);

    // Scroll to results
    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleReset = () => {
    setSelectedSymptoms([]);
    setShowResults(false);
    setResults(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-full p-3">
              <Heart className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Symptom Check-In
          </h1>
          <p className="text-gray-600">
            Understand what your body might be telling you
          </p>
        </div>

        {/* Important Notice */}
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-900">
                  <strong>Important:</strong> This tool helps you understand possible lifestyle factors. 
                  It does not diagnose medical conditions. Always trust your instincts and seek 
                  professional help when needed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Select Symptoms */}
        <Card className="border-2 border-purple-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-900">
              <span className="bg-purple-100 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</span>
              What are you experiencing?
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Select all that apply
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-3">
              {availableSymptoms.map((symptom) => (
                <div
                  key={symptom.id}
                  className={`
                    p-4 rounded-lg border-2 cursor-pointer transition-all
                    ${selectedSymptoms.includes(symptom.id)
                      ? 'bg-purple-50 border-purple-400'
                      : 'bg-white border-gray-200 hover:border-purple-200'
                    }
                  `}
                  onClick={() => handleSymptomToggle(symptom.id)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedSymptoms.includes(symptom.id)}
                      onCheckedChange={() => handleSymptomToggle(symptom.id)}
                    />
                    <span className="text-2xl">{symptom.icon}</span>
                    <span className="font-medium text-gray-900">{symptom.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Lifestyle Context */}
        <Card className="border-2 border-blue-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <span className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
              Your habits today
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              This helps us understand context
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick Yes/No Questions */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Slept well last night?</label>
                <div className="flex gap-2">
                  <Button
                    variant={lifestyleContext.slept_well ? 'default' : 'outline'}
                    onClick={() => setLifestyleContext(prev => ({ ...prev, slept_well: true }))}
                    className="flex-1"
                  >
                    Yes
                  </Button>
                  <Button
                    variant={!lifestyleContext.slept_well ? 'default' : 'outline'}
                    onClick={() => setLifestyleContext(prev => ({ ...prev, slept_well: false }))}
                    className="flex-1"
                  >
                    No
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Drank enough water?</label>
                <div className="flex gap-2">
                  <Button
                    variant={lifestyleContext.drank_water ? 'default' : 'outline'}
                    onClick={() => setLifestyleContext(prev => ({ ...prev, drank_water: true }))}
                    className="flex-1"
                  >
                    Yes
                  </Button>
                  <Button
                    variant={!lifestyleContext.drank_water ? 'default' : 'outline'}
                    onClick={() => setLifestyleContext(prev => ({ ...prev, drank_water: false }))}
                    className="flex-1"
                  >
                    No
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Moved your body?</label>
                <div className="flex gap-2">
                  <Button
                    variant={lifestyleContext.moved_body ? 'default' : 'outline'}
                    onClick={() => setLifestyleContext(prev => ({ ...prev, moved_body: true }))}
                    className="flex-1"
                  >
                    Yes
                  </Button>
                  <Button
                    variant={!lifestyleContext.moved_body ? 'default' : 'outline'}
                    onClick={() => setLifestyleContext(prev => ({ ...prev, moved_body: false }))}
                    className="flex-1"
                  >
                    No
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Ate balanced meals?</label>
                <div className="flex gap-2">
                  <Button
                    variant={lifestyleContext.ate_well ? 'default' : 'outline'}
                    onClick={() => setLifestyleContext(prev => ({ ...prev, ate_well: true }))}
                    className="flex-1"
                  >
                    Yes
                  </Button>
                  <Button
                    variant={!lifestyleContext.ate_well ? 'default' : 'outline'}
                    onClick={() => setLifestyleContext(prev => ({ ...prev, ate_well: false }))}
                    className="flex-1"
                  >
                    No
                  </Button>
                </div>
              </div>
            </div>

            {/* Stress Level */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Stress level today:</label>
              <div className="flex gap-2">
                <Button
                  variant={lifestyleContext.stress_level === 'low' ? 'default' : 'outline'}
                  onClick={() => setLifestyleContext(prev => ({ ...prev, stress_level: 'low' }))}
                  className="flex-1"
                >
                  Low
                </Button>
                <Button
                  variant={lifestyleContext.stress_level === 'moderate' ? 'default' : 'outline'}
                  onClick={() => setLifestyleContext(prev => ({ ...prev, stress_level: 'moderate' }))}
                  className="flex-1"
                >
                  Moderate
                </Button>
                <Button
                  variant={lifestyleContext.stress_level === 'high' ? 'default' : 'outline'}
                  onClick={() => setLifestyleContext(prev => ({ ...prev, stress_level: 'high' }))}
                  className="flex-1"
                >
                  High
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Analyze Button */}
        <div className="flex justify-center">
          <Button
            onClick={handleAnalyze}
            disabled={selectedSymptoms.length === 0}
            className="h-14 px-8 text-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            Analyze Symptoms
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>

        {/* Results Section */}
        {showResults && results && (
          <div id="results-section" className="space-y-6 mt-8">
            
            {/* Summary */}
            <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-purple-900 mb-4">
                  {results.summary}
                </h3>
                
                {results.insights.length > 0 ? (
                  <div className="space-y-3">
                    {results.insights.map((insight, index) => (
                      <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg border border-purple-200">
                        <AlertCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <p className="text-gray-800">{insight.text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-700">
                    Your symptoms don't match clear lifestyle patterns. Consider tracking for a few days.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Simple Actions */}
            {results.actions.length > 0 && (
              <Card className="border-2 border-green-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-900">
                    <Lightbulb className="h-6 w-6 text-green-600" />
                    Simple Actions You Can Take Now
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {results.actions.map((action, index) => (
                      <div key={index} className="flex items-start gap-3 bg-green-50 p-4 rounded-lg border-2 border-green-200">
                        <span className="text-2xl flex-shrink-0">{action.icon}</span>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{action.text}</p>
                        </div>
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* When to Seek Help */}
            <Card className={`border-2 shadow-lg ${
              results.seekHelp.urgency === 'high' 
                ? 'border-red-200 bg-red-50' 
                : results.seekHelp.urgency === 'medium'
                ? 'border-orange-200 bg-orange-50'
                : 'border-blue-200 bg-blue-50'
            }`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${
                  results.seekHelp.urgency === 'high' ? 'text-red-900' : 
                  results.seekHelp.urgency === 'medium' ? 'text-orange-900' : 
                  'text-blue-900'
                }`}>
                  <Phone className="h-6 w-6" />
                  {results.seekHelp.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {results.seekHelp.guidance.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-lg flex-shrink-0">•</span>
                      <span className="text-gray-800">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Medical Disclaimer */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-600 text-center">
                <strong>Disclaimer:</strong> This tool provides general wellness guidance based on lifestyle factors. 
                It does not replace professional medical advice, diagnosis, or treatment. Always consult a 
                healthcare provider for medical concerns.
              </p>
            </div>

            {/* Reset Button */}
            <div className="flex justify-center">
              <Button onClick={handleReset} variant="outline" className="border-2">
                Start New Check-In
              </Button>
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="flex justify-center mt-8">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="border-2 border-purple-300 hover:bg-purple-50"
          >
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SymptomChecker;
